<template>
  <div class="vlw__header">
    <PrimaryHeader :style="appEnvStyle">
      <!-- Left -->
      <template v-slot:left>
        <div v-if="appVersion" v-html="appVersion" />
      </template>

      <!-- Right -->
      <template v-slot:right>
        <div class="vl-action-group vl-action-group--bordered">
          <!-- Application Links-->
          <VlLink mod-external :href="linkToCRM" rel="noopener noreferrer" target="_blank">CRM</VlLink>
          <VlLink mod-external :href="linkToGenesys" rel="noopener noreferrer" target="_blank">Genesys</VlLink>

          <!-- CTI Status / Login -->
          <template v-if="user.isLoggedIn">
            <CtiStatus />
            
            <StatusTimer class="ml-4 vl-pill" />
          </template>

          <CtiLogin v-else class="ml-4" />
        </div>

        <UserDropdown 
          @open-settings="isSettingsOpen = true" 
          @open-statistics="isStatisticsOpen = true" 
          @logout="handleLogOut" 
        />
      </template>
    </PrimaryHeader>

    <CTIToolBar />
    <UserSettings v-if="isSettingsOpen" @close="isSettingsOpen = false" />
    <UserStatistics v-if="isStatisticsOpen" @close="isStatisticsOpen = false" />
  </div>
</template>

<script>
import { identity } from 'lodash';
import { mapState, mapActions, mapGetters } from 'vuex';
import bus from '@/utils/bus';

import PrimaryHeader from '@/components/header/PrimaryHeader';
import CtiLogin from './cti/LoginButton';
import CtiStatus from '@/components/header/cti/StatusDropdown.vue';
import StatusTimer from '@/components/StatusTimer';
import clickOutside from '@/helpers/click-outside';

import UserSettings from '@/components/header/UserSettings';
import UserStatistics from '@/components/header/UserStatistics.vue';
import UserDropdown from './UserDropdown.vue';

import CTIToolBar from '@/components/header/CTIToolbar';

const {
  VUE_APP_CRM_URL,
  VUE_APP_ENV,
  VUE_APP_ENV_COLOR,
  VUE_APP_VERSION,
  VUE_APP_CTI_MPC_URL,
} = process.env;

const LOG_OUT = 'Log out';

export default {
  components: {
    CTIToolBar,
    CtiLogin,
    CtiStatus,
    PrimaryHeader,
    StatusTimer,
    UserDropdown,
    UserSettings,
    UserStatistics,
  },

  directives: {
    clickOutside,
  },
  data() {
    return {
      isSettingsOpen: false,
      isStatisticsOpen: false,
    };
  },

  computed: {
    ...mapGetters('cti', ['isConnected', 'isAlerting']),
    ...mapGetters('authentication', ['isLoggedIn', 'userName']),
    ...mapState('cti', ['user']),
    ...mapGetters('metadata', ['getLabelByName']),
    ...mapGetters('contact', ['contactId', 'hasClosedStatus', 'cannotBeClosedBecause']),

    isDarkMode: {
      get() {
        return this.$store.state.ui.isDarkMode;
      },

      set(value) {
        this.$store.commit('ui/UPDATE_DARK_MODE', value);
      },
    },

    appVersion() {
      return `v${[VUE_APP_VERSION, VUE_APP_ENV]
        .filter(identity)
        .join(' &mdash; ')}`;
    },

    appEnvStyle() {
      return VUE_APP_ENV_COLOR ? `background: ${VUE_APP_ENV_COLOR};` : null;
    },

    linkToCRM() {
      return VUE_APP_CRM_URL;
    },

    linkToGenesys() {
      return VUE_APP_CTI_MPC_URL;
    },
    userInitials() {
      if (!this.userName) return '';

      const words = this.userName.trim().split(' ').filter(Boolean);

      if (words.length === 1) {
        return words[0].charAt(0).toUpperCase();
      }

      return (words[0][0] + words[words.length - 1][0]).toUpperCase();
    },
  },

  methods: {
    ...mapActions('authentication', {
      crmLogOut: 'logOut',
    }),

    // Triggered when user confirms logout
    async confirmLogOut() {
      try {
        await this.$store.dispatch('cti/updateStatus', 'Niet aan het werk');
      } catch (error) {
        console.error('Update status failed:', error);
      }

      this.crmLogOut();
    },
    
    // Triggered when clicking the logout button
    handleLogOut() {
      // Prevent logout if currently connected or alerting
      if (this.isConnected || this.isAlerting) {
        const label = this.getLabelByName('connected_logout');

        return bus.$emit('show-modal', {
          message: label.title,
          body: label.body,
          confirmText: label.confirm,
          confirm: true,
        });
      }

      /* Prevent logout if a contact is still open
      if (this.contactId) {
        if (!this.hasClosedStatus && this.cannotBeClosedBecause) {
          const label = this.getLabelByName('connected_logout');

          return bus.$emit('show-modal', {
            message: label.title,
            body: this.cannotBeClosedBecause
          });
        } else {
          const label = this.getLabelByName('connected_logout');
          return bus.$emit('show-modal', {
            message: label.title,
            body: 'Je huidig contact is niet gesloten. Sluit het contact voor je afmeldt.',
          });
        }
      }
        */

      // Ask user to confirm logout
      const label = this.getLabelByName('confirm_logout');

      bus.$emit('show-modal', {
        message: label.title,
        body: label.body,
        confirmText: label.confirm,
        confirm: this.confirmLogOut,
        cancel: true,
        cancelText: label.cancel,
      });
    },
    openNewWindow() {
      window.open(this.linkToGenesys, '_blank', 'fullscreen=yes');
    },
  },
};
</script>

<style lang="scss">
.vlw__header__user-image {
  background: #d8d8d8;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  margin-right: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  color: #555;
  text-transform: uppercase;
  overflow: hidden;
}

.vlw__header__user-name {
  line-height: 24px;
  text-transform: uppercase;
  font-weight: 500;
}

.primary-header__right .icon-offset {
  margin-top: 10px;
}
</style>
